import { View, StyleSheet, Text, TextInput } from "react-native";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import { Bot, MessageSquare, RotateCcw } from "lucide-react";
import { Info as InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import AdminCompanionDocumentCardList from "../../components/adminDashboard/AdminCompanionDocumentCardList/AdminCompanionDocumentCardList";
import {
  DocumentStatus as ApiDocumentStatus,
  StoragePathType,
  ExternalSourceChunkingStrategyInput,
  useGetDocumentLazyQuery,
  useDeleteAdminDocumentMutation,
  useGetAdminDocumentsForTenantQuery,
  useCreateAdminDocumentMutation,
  useUpdateAdminDocumentMutation,
  useUpdateTenantSettingsMutation,
} from "@axio/graphql/generated/graphql";

import { formatSubmittedDate } from "../../utils/date";
import AdminCompanionDocumentCard, {
  DocumentData,
} from "../../components/adminDashboard/AdminCompanionDocumentCard/AdminCompanionDocumentCard";
import { AxioActionModal } from "../../components/axio-ds/Modal/AxioModal";
import { colors, spacing, radii, typographyScale } from "../../styles/design-system";
import { ContentPicker } from "../../components/common/general/ContentPicker/ContentPicker";
import { ExternalPickedItem, PickedItem } from "../../components/common/general/ContentPicker/ContentPicker.types";
import { useAttachExternalContent } from "../../hooks/contentPicker/useAttachExternalContent";
import { ADMIN_KNOWLEDGE_BASE_CONTEXT } from "../../utils/contentPicker/contentPickerContextPresets";
import { useExternalIngestionStatus } from "../../hooks/contentPicker/useExternalIngestionStatus";
import {
  convertExternalPickedItemsToAttachmentInput,
  pairExternalItemsWithQueuedJobs,
} from "../../utils/contentPicker/externalItemAdapter";

import CustomButton from "../../components/common/general/CustomButton/CustomButton";

const POLL_INTERVAL_MS = 4000;

const mapApiStatusToUi = (status: ApiDocumentStatus): DocumentData["status"] => {
  switch (status) {
    case ApiDocumentStatus.Completed:
      return "ready";
    case ApiDocumentStatus.Failed:
      return "failed";
    case ApiDocumentStatus.Pending:
    case ApiDocumentStatus.Processing:
    default:
      return "processing";
  }
};

const mapAdminDocumentToCard = (document: {
  id: string;
  title?: string | null;
  status: ApiDocumentStatus;
  isPolicy?: boolean | null;
  created?: string | null;
  files?: Array<{ id: string; fileName?: string | null }> | null;
}): DocumentData => {
  const primaryFile = document.files?.[0];
  return {
    id: document.id,
    documentId: document.id,
    fileId: primaryFile?.id,
    filename: primaryFile?.fileName || document.title || "Document",
    size: "Unknown size",
    uploadedDate: formatSubmittedDate(document.created),
    status: mapApiStatusToUi(document.status),
    isPolicy: document.isPolicy ?? false,
  };
};

interface AdminCompanionSettingsContainerProps {
  initialAiDisclaimerText?: string;
  onDisclaimerSaved?: () => void;
}

function AdminCompanionSettingsContainer({
  initialAiDisclaimerText,
  onDisclaimerSaved,
}: AdminCompanionSettingsContainerProps) {
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "removePolicy";
    id: string;
    filename: string;
  } | null>(null);

  // ContentPicker state
  const [externalJobIds, setExternalJobIds] = useState<string[]>([]);
  const prevPickedRef = useRef<PickedItem[]>([]);
  const processedExternalJobs = useRef<Set<string>>(new Set());

  // Hooks for external content
  const { attachExternal } = useAttachExternalContent();
  const { jobStatuses } = useExternalIngestionStatus(externalJobIds);

  // AI Disclaimer state
  const [disclaimerText, setDisclaimerText] = useState(initialAiDisclaimerText || "");
  const [originalDisclaimerText, setOriginalDisclaimerText] = useState(initialAiDisclaimerText || "");
  const [disclaimerSaving, setDisclaimerSaving] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState("");
  const [disclaimerSuccess, setDisclaimerSuccess] = useState("");
  const [updateTenantSettings] = useUpdateTenantSettingsMutation();
  const disclaimerHasChanges = disclaimerText !== originalDisclaimerText;

  useEffect(() => {
    setDisclaimerText(initialAiDisclaimerText || "");
    setOriginalDisclaimerText(initialAiDisclaimerText || "");
  }, [initialAiDisclaimerText]);

  const handleSaveDisclaimer = useCallback(async () => {
    setDisclaimerSaving(true);
    setDisclaimerError("");
    setDisclaimerSuccess("");
    try {
      const result = await updateTenantSettings({
        variables: {
          settings: {
            aiDisclaimerText: disclaimerText,
          },
        },
      });
      if (!result.data?.updateTenantSettings?.success) {
        throw new Error(result.data?.updateTenantSettings?.message || "Failed to save disclaimer");
      }
      setOriginalDisclaimerText(disclaimerText);
      setDisclaimerSuccess("Disclaimer saved successfully.");
      onDisclaimerSaved?.();
    } catch (error) {
      setDisclaimerError(error instanceof Error ? error.message : "Failed to save disclaimer");
    } finally {
      setDisclaimerSaving(false);
    }
  }, [disclaimerText, updateTenantSettings, onDisclaimerSaved]);

  const handleResetDisclaimer = useCallback(() => {
    setDisclaimerText("");
    setDisclaimerError("");
    setDisclaimerSuccess("");
  }, []);

  const [createAdminDocument] = useCreateAdminDocumentMutation();
  const [deleteAdminDocument] = useDeleteAdminDocumentMutation();
  const [updateAdminDocument] = useUpdateAdminDocumentMutation();
  const [getDocument] = useGetDocumentLazyQuery({
    fetchPolicy: "network-only",
  });
  const { data: adminDocumentsData, refetch: refetchAdminDocuments } = useGetAdminDocumentsForTenantQuery({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setDocuments((prev) => {
      const serverDocs = (adminDocumentsData?.getAdminDocumentsForTenant?.data || []).map((document) =>
        mapAdminDocumentToCard(document)
      );
      const optimisticDocs = prev.filter((d) => d.externalJobId);
      return [...optimisticDocs, ...serverDocs];
    });
  }, [adminDocumentsData]);

  useEffect(() => {
    if (jobStatuses.size === 0) return;

    jobStatuses.forEach((jobStatus, jobId) => {
      if (jobStatus.isTerminal && !processedExternalJobs.current.has(jobId)) {
        processedExternalJobs.current.add(jobId);
        const doc = documents.find((d) => d.externalJobId === jobId);
        const fileLabel = doc?.filename || jobStatus.file?.fileName || "File";

        if (jobStatus.category === "completed" && jobStatus.file?.id) {
          createAdminDocument({
            variables: {
              fileId: jobStatus.file.id,
              title: doc?.filename || jobStatus.file.fileName || "External Document",
            },
          })
            .then(() => {
              setDocuments((prev) => prev.filter((d) => d.externalJobId !== jobId));
              refetchAdminDocuments();
            })
            .catch((error) => {
              const message = error instanceof Error ? error.message : "Upload failed.";
              setUploadError(message);
              setDocuments((prev) => prev.filter((d) => d.externalJobId !== jobId));
            });
        } else if (jobStatus.category === "completed" && !jobStatus.file?.id) {
          setUploadError(`${fileLabel} could not be processed: no uploaded file was returned.`);
          setDocuments((prev) => prev.filter((d) => d.externalJobId !== jobId));
        } else if (jobStatus.category === "failed") {
          const reason = jobStatus.failedReason?.trim();
          setUploadError(
            reason ? `${fileLabel} could not be processed: ${reason}` : `${fileLabel} could not be processed.`
          );
          setDocuments((prev) => prev.filter((d) => d.externalJobId !== jobId));
        }
      }
    });
  }, [jobStatuses, createAdminDocument, documents, refetchAdminDocuments]);

  const documentsToPoll = useMemo(
    () =>
      documents.filter(
        (doc) => doc.documentId && !doc.externalJobId && doc.status !== "ready" && doc.status !== "failed"
      ),
    [documents]
  );

  const externalProcessingDocs = useMemo(() => documents.filter((doc) => doc.externalJobId), [documents]);
  const mainDocuments = useMemo(() => documents.filter((doc) => !doc.externalJobId), [documents]);

  useEffect(() => {
    if (documentsToPoll.length === 0) return;
    let isActive = true;

    const pollStatuses = async () => {
      const results = await Promise.all(
        documentsToPoll.map(async (doc) => {
          try {
            const response = await getDocument({ variables: { id: doc.documentId } });
            return { documentId: doc.documentId, data: response.data?.getDocument?.data };
          } catch {
            return { documentId: doc.documentId, data: null };
          }
        })
      );

      if (!isActive) return;

      await refetchAdminDocuments();
    };

    pollStatuses();
    const interval = setInterval(pollStatuses, POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [documentsToPoll, getDocument]);

  const handleDeleteRequest = useCallback(
    (id: string) => {
      const doc = documents.find((d) => d.id === id);
      setConfirmAction({ type: "delete", id, filename: doc?.filename ?? "this document" });
    },
    [documents]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === id
            ? {
                ...doc,
                status: "deleting",
                statusText: "Deleting...",
              }
            : doc
        )
      );

      try {
        const result = await deleteAdminDocument({ variables: { id } });
        if (!result.data?.deleteAdminDocument?.success) {
          throw new Error(result.data?.deleteAdminDocument?.message || "Failed to delete document.");
        }
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to delete document.";
        setUploadError(message);
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === id
              ? {
                  ...doc,
                  status: "ready",
                  statusText: undefined,
                }
              : doc
          )
        );
      }
    },
    [deleteAdminDocument, documents]
  );

  const handleTogglePolicy = useCallback(
    async (id: string, isPolicy: boolean) => {
      // Optimistically update the UI
      setDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, isPolicy } : doc)));

      try {
        const result = await updateAdminDocument({
          variables: { id, isPolicy },
        });
        if (!result.data?.updateAdminDocument?.success) {
          throw new Error(result.data?.updateAdminDocument?.message || "Failed to update document.");
        }
      } catch (error) {
        // Revert on failure
        setDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, isPolicy: !isPolicy } : doc)));
        const message = error instanceof Error ? error.message : "Failed to update document.";
        setUploadError(message);
      }
    },
    [updateAdminDocument]
  );

  const handleTogglePolicyRequest = useCallback(
    (id: string, isPolicy: boolean) => {
      if (!isPolicy) {
        // Removing policy — confirm first
        const doc = documents.find((d) => d.id === id);
        setConfirmAction({ type: "removePolicy", id, filename: doc?.filename ?? "this document" });
        return;
      }
      // Adding policy — no confirmation needed
      handleTogglePolicy(id, isPolicy);
    },
    [documents, handleTogglePolicy]
  );

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction) return;
    if (confirmAction.type === "delete") {
      handleDelete(confirmAction.id);
    } else {
      handleTogglePolicy(confirmAction.id, false);
    }
    setConfirmAction(null);
  }, [confirmAction, handleDelete, handleTogglePolicy]);

  // Handle external item selection from ContentPicker
  const handleFilesSelected = useCallback(
    async (items: PickedItem[]) => {
      setUploadError("");

      const prevItems = prevPickedRef.current;
      const newItems = items.slice(prevItems.length);
      prevPickedRef.current = items;

      if (newItems.length === 0) return;

      const localItems = newItems.filter((item) => item.kind === "local");
      if (localItems.length > 0) {
        for (const item of localItems) {
          try {
            const result = await createAdminDocument({
              variables: {
                fileId: item.uploadedFileId!,
                title: item.name,
              },
            });
            const created = result.data?.createAdminDocument?.data;
            if (!result.data?.createAdminDocument?.success || !created?.id) {
              throw new Error(result.data?.createAdminDocument?.message || "Failed to create admin document.");
            }
          } catch (error) {
            const message = error instanceof Error ? error.message : "Upload failed.";
            setUploadError(message);
          }
        }
        await refetchAdminDocuments();
      }

      const externalItems = newItems.filter((item): item is ExternalPickedItem => item.kind === "external");
      if (externalItems.length > 0) {
        try {
          // Convert and validate items
          const { grouped, rejected } = convertExternalPickedItemsToAttachmentInput(externalItems);

          // Show rejected items as errors
          if (rejected.length > 0) {
            const rejectedNames = rejected.map((r) => r.item.name).join(", ");
            setUploadError(`Some items could not be added: ${rejectedNames}`);
          }

          // Attach valid items via mutation
          const allJobIds: string[] = [];
          for (const group of grouped) {
            const result = await attachExternal({
              connectorType: group.connectorType,
              externalItemIds: group.externalItemIds,
              strategy: ExternalSourceChunkingStrategyInput.UnstructuredWithImages, // Better for knowledge base
            });

            const itemJobPairs = pairExternalItemsWithQueuedJobs(
              group.items,
              result?.attachExternalContent?.data?.itemResults ?? []
            );
            allJobIds.push(...itemJobPairs.map(({ jobId }) => jobId));

            // Add optimistic document entries to UI
            const newDocuments: DocumentData[] = itemJobPairs.map(({ item, jobId }, index) => ({
              id: `external-${item.externalId}-${jobId}-${index}`,
              documentId: `external-${item.externalId}-${jobId}-${index}`,
              fileId: item.externalId,
              filename: item.name,
              size: item.sizeBytes ? `${(item.sizeBytes / 1024).toFixed(2)} KB` : "Unknown size",
              uploadedDate: formatSubmittedDate(new Date().toISOString()),
              status: "processing",
              isPolicy: false,
              externalJobId: jobId,
            }));

            setDocuments((prev) => [...newDocuments, ...prev]);
          }

          // Track job IDs for polling
          setExternalJobIds((prev) => [...prev, ...allJobIds]);
        } catch (error) {
          console.error("Error importing external items:", error);
          setUploadError("Failed to import selected items. Please try again.");
        }
      }
    },
    [attachExternal, createAdminDocument, refetchAdminDocuments]
  );

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Bot size={20} color="#7C3AED" />
          </View>
          <View style={styles.headerContent}>
            <CustomText
              text="Knowledge Base"
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.title}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text="Manage the documents that power the AI Companion's knowledge base."
              size="s"
              useTranslationText={false}
              style={styles.description}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        {/* ContentPicker Integration */}
        <ContentPicker
          onFilesSelected={handleFilesSelected}
          multiSelect={true}
          storagePath={StoragePathType.AdminDocuments}
          isSelectingList={false}
          isPublic={false}
          allowLinks={false}
          context={ADMIN_KNOWLEDGE_BASE_CONTEXT}
        />

        <CustomSpacing type="vertical" size="l" />

        {uploadError ? (
          <>
            <CustomText text={uploadError} size="xs" useTranslationText={false} style={styles.errorText} />
            <CustomSpacing type="vertical" size="s" />
          </>
        ) : null}

        <CustomSpacing type="vertical" size="l" />

        {/* External Processing List */}
        {externalProcessingDocs.length > 0 && (
          <View style={{ marginBottom: 24 }}>
            <CustomText
              text="Importing External Resources"
              size="s"
              weight="bold"
              useTranslationText={false}
              style={{ color: "#374151", marginBottom: 12 }}
            />
            <View style={{ gap: 8 }}>
              {externalProcessingDocs.map((doc) => (
                <AdminCompanionDocumentCard key={doc.id} document={doc} />
              ))}
            </View>
          </View>
        )}

        {/* Document List */}
        <AdminCompanionDocumentCardList
          documents={mainDocuments}
          onDelete={handleDeleteRequest}
          onTogglePolicy={handleTogglePolicyRequest}
        />

        <CustomSpacing type="vertical" size="l" />

        {/* Help Card */}
        <View style={styles.helpCard}>
          <InfoIcon size={20} color="rgb(7, 89, 133)" weight="regular" style={styles.helpIcon} />
          <View style={styles.helpContent}>
            <CustomText
              text="How the Knowledge Base Works"
              size="m"
              weight="bold"
              useTranslationText={false}
              style={styles.helpTitle}
            />
            <CustomSpacing type="vertical" size="xs" />
            <View style={styles.helpList}>
              <CustomText
                text="• Uploaded documents are processed and ready to use within about a minute"
                size="s"
                useTranslationText={false}
                style={styles.helpListItem}
              />
              <CustomText
                text="• Students can ask the AI Companion questions about your uploaded content, such as handbooks, FAQs, or guidelines"
                size="s"
                useTranslationText={false}
                style={styles.helpListItem}
              />
              <CustomText
                text="• Documents marked as Policy are always applied — the AI will follow your institution's rules in every response"
                size="s"
                useTranslationText={false}
                style={styles.helpListItem}
              />
            </View>
          </View>
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* AI Disclaimer */}
      <View style={styles.card}>
        <View style={styles.disclaimerHeaderRow}>
          <View style={[styles.iconContainer, { backgroundColor: "#F3E8FF" }]}>
            <MessageSquare size={20} color="#7C3AED" />
          </View>
          <View style={styles.disclaimerHeaderText}>
            <CustomText
              text="AI Disclaimer"
              size="l"
              weight="bold"
              useTranslationText={false}
              style={{ color: "#111827" }}
            />
            <CustomText
              text="Shown beneath the AI Companion's chat input on every conversation. Customize the wording to match your institution's policies, or leave it blank to use the Axio default."
              size="s"
              useTranslationText={false}
              style={{ color: "#6B7280", lineHeight: 20 }}
            />
          </View>
        </View>

        <View style={styles.disclaimerFieldGroup}>
          <CustomText
            text="Disclaimer text"
            size="s"
            weight="medium"
            useTranslationText={false}
            style={{ color: "#6B7280" }}
          />
          <TextInput
            style={styles.disclaimerTextarea}
            value={disclaimerText}
            onChangeText={(text) => {
              setDisclaimerText(text);
              setDisclaimerSuccess("");
              setDisclaimerError("");
            }}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
            placeholder="Enter custom disclaimer text..."
            placeholderTextColor="#9CA3AF"
          />
          <CustomText
            text="Plain text only. Appears in full beneath the chat composer. Leave blank to use the Axio default."
            size="xs"
            useTranslationText={false}
            style={{ color: "#9CA3AF" }}
          />
        </View>

        {disclaimerError ? (
          <CustomText
            text={disclaimerError}
            size="xs"
            useTranslationText={false}
            style={{ color: "#B91C1C", marginTop: 8 }}
          />
        ) : null}

        {disclaimerSuccess ? (
          <CustomText
            text={disclaimerSuccess}
            size="xs"
            useTranslationText={false}
            style={{ color: "#16A34A", marginTop: 8 }}
          />
        ) : null}

        <View style={styles.disclaimerActions}>
          {disclaimerText.trim().length > 0 && (
            <CustomButton
              text="Reset to default"
              styleType="light"
              size="s"
              onPress={handleResetDisclaimer}
              leftIcon={<RotateCcw size={14} color="#6B7280" />}
              rightIcon={null}
              useTranslationText={false}
              style={{ borderWidth: 0 }}
              textStyle={{ color: "#6B7280", fontSize: 13 }}
            />
          )}
          {disclaimerHasChanges && (
            <CustomButton
              text={disclaimerSaving ? "Saving..." : "Save Disclaimer"}
              styleType="primary"
              size="s"
              onPress={handleSaveDisclaimer}
              bold
              leftIcon={null}
              rightIcon={null}
              useTranslationText={false}
              disabled={disclaimerSaving}
            />
          )}
        </View>
      </View>

      <AxioActionModal
        visible={confirmAction?.type === "delete"}
        onRequestClose={() => setConfirmAction(null)}
        title="Delete Document"
        primaryActionLabel="Delete"
        onPrimaryAction={handleConfirmAction}
        secondaryActionLabel="Cancel"
        onSecondaryAction={() => setConfirmAction(null)}
        ariaLabel="Delete Document Confirmation">
        <Text style={styles.confirmText}>
          Are you sure you want to delete &quot;{confirmAction?.filename}&quot;? This action cannot be undone.
        </Text>
      </AxioActionModal>

      <AxioActionModal
        visible={confirmAction?.type === "removePolicy"}
        onRequestClose={() => setConfirmAction(null)}
        title="Remove Policy Enforcement"
        primaryActionLabel="Remove Policy"
        onPrimaryAction={handleConfirmAction}
        secondaryActionLabel="Cancel"
        onSecondaryAction={() => setConfirmAction(null)}
        ariaLabel="Remove Policy Confirmation">
        <Text style={styles.confirmText}>
          &quot;{confirmAction?.filename}&quot; will no longer be automatically enforced in AI Companion responses. It
          will still be available as a searchable document.
        </Text>
      </AxioActionModal>
    </View>
  );
}

const styles = StyleSheet.create({
  confirmText: {
    fontSize: 14,
    color: colors.grey[600],
    textAlign: "center",
    lineHeight: 20,
  },
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 16,
    backgroundColor: "#F3E8FF",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    color: "#111827",
  },
  description: {
    color: "#475467",
  },
  helpCard: {
    backgroundColor: colors.blue[100],
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.blue[500],
    padding: spacing.lg,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  helpIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  helpContent: {
    flex: 1,
  },
  helpTitle: {
    color: colors.grey[900],
  },
  helpList: {
    gap: 6,
  },
  helpListItem: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
  errorText: {
    color: "#B91C1C",
  },
  disclaimerHeaderRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 24,
  },
  disclaimerHeaderText: {
    flex: 1,
    gap: 4,
  },
  disclaimerFieldGroup: {
    gap: 6,
  },
  disclaimerTextarea: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: "#111827",
    minHeight: 96,
  },
  disclaimerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },
});

export default AdminCompanionSettingsContainer;
